import { useMemo, useState } from 'react';

import type {
  CreateSavedListOutreachQueueRequestDTO,
  CreatorsInListDTO,
  SavedListOutreachChannelDTO,
  SavedListOutreachMessageDTO,
} from '@/entities/saved-creators-list';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';

export const EMAIL_TEMPLATE_OPTIONS = [
  'Beauty seeding',
  'Wellness partnership',
  'Fashion styling collab',
  'UGC testimonial request',
];
export const TIKTOK_DM_TEMPLATE_OPTIONS = [
  'TikTok warm intro',
  'TikTok product seeding',
  'Creator event invite',
  'Short-form UGC request',
];

const DEFAULT_INITIAL_EMAIL: SavedListOutreachMessageDTO = {
  subject: 'Beauty product seeding with {{brand_name}}',
  body: "Hi {{name}},\n\nI'm reaching out from {{brand_name}} because your beauty content feels like a strong fit for our upcoming product seeding campaign. We'd love to send you a curated beauty product set and explore an honest feature if it feels natural for your audience.\n\nIf you're open to it, I can share the product details, timeline, and deliverable options.\n\nBest,\n{{sender_name}}",
};

const DEFAULT_FOLLOW_UP_EMAIL: SavedListOutreachMessageDTO = {
  subject: 'Following up on the beauty seeding invite',
  body: "Hi {{name}},\n\nI wanted to follow up on the beauty product seeding note I sent earlier. The {{brand_name}} team thinks your audience could be a great match, and we'd be happy to keep the collaboration simple: product gifting first, then content only if it feels like a genuine fit.\n\nWould you be open to reviewing the details?\n\nBest,\n{{sender_name}}",
};

const DEFAULT_INITIAL_DM: SavedListOutreachMessageDTO = {
  body: "Hi {{name}}, I hope this message finds you well! I'm reaching out from {{brand_name}} because I believe there could be a great collaboration opportunity between us.",
};

type RecipientParams = {
  channel: SavedListOutreachChannelDTO;
  creators: CreatorsInListDTO[];
  selectedCreatorIds: string[];
};

export function getSavedListOutreachRecipientIds({
  channel,
  creators,
  selectedCreatorIds,
}: RecipientParams): string[] {
  const selectedIdSet = new Set(selectedCreatorIds);

  return creators
    .filter((creator) => selectedIdSet.has(creator.id))
    .filter((creator) => {
      if (channel === 'email') {
        return Boolean(creator.email);
      }

      return creator.platform === SOURCE_PLATFORM_TYPE.TIKTOK;
    })
    .map((creator) => creator.id);
}

export function getSavedListOutreachRecipientLabel({
  channel,
  selectedCount,
  recipientCount,
}: {
  channel: SavedListOutreachChannelDTO;
  selectedCount: number;
  recipientCount: number;
}) {
  if (selectedCount === recipientCount) {
    return `${recipientCount}`;
  }

  const channelLabel = channel === 'email' ? 'with email' : 'TikTok creators';
  return `${recipientCount} of ${selectedCount} ${channelLabel}`;
}

export function useSavedListOutreachState({
  creators,
  selectedCreatorIds,
}: {
  creators: CreatorsInListDTO[];
  selectedCreatorIds: string[];
}) {
  const [channel, setChannel] = useState<SavedListOutreachChannelDTO>('email');
  const [emailConnected, setEmailConnected] = useState(true);
  const [tiktokDmConnected, setTiktokDmConnected] = useState(false);
  const [emailTemplateName, setEmailTemplateName] = useState(EMAIL_TEMPLATE_OPTIONS[0]);
  const [tiktokDmTemplateName, setTiktokDmTemplateName] = useState(TIKTOK_DM_TEMPLATE_OPTIONS[0]);
  const [initialEmail, setInitialEmail] =
    useState<SavedListOutreachMessageDTO>(DEFAULT_INITIAL_EMAIL);
  const [followUpEmail, setFollowUpEmail] =
    useState<SavedListOutreachMessageDTO>(DEFAULT_FOLLOW_UP_EMAIL);
  const [initialDm, setInitialDm] = useState<SavedListOutreachMessageDTO>(DEFAULT_INITIAL_DM);
  const [followUpEnabled, setFollowUpEnabled] = useState(true);
  const [aiRepliesEnabled, setAiRepliesEnabled] = useState(true);

  const recipientIds = useMemo(
    () => getSavedListOutreachRecipientIds({ channel, creators, selectedCreatorIds }),
    [channel, creators, selectedCreatorIds],
  );
  const templateName = channel === 'email' ? emailTemplateName : tiktokDmTemplateName;
  const initialMessage = channel === 'email' ? initialEmail : initialDm;
  const isConnected = channel === 'email' ? emailConnected : tiktokDmConnected;

  const request = useMemo<CreateSavedListOutreachQueueRequestDTO | null>(() => {
    if (recipientIds.length === 0) {
      return null;
    }

    return {
      channel,
      creator_ids: recipientIds,
      sequence_name: templateName,
      initial_message: initialMessage,
      follow_up:
        channel === 'email'
          ? {
              enabled: followUpEnabled,
              subject: followUpEmail.subject,
              body: followUpEmail.body,
              delay_days: 3,
            }
          : undefined,
      ai_replies_enabled: channel === 'email' ? aiRepliesEnabled : false,
    };
  }, [
    aiRepliesEnabled,
    channel,
    followUpEmail.body,
    followUpEmail.subject,
    followUpEnabled,
    initialMessage,
    recipientIds,
    templateName,
  ]);

  return {
    channel,
    setChannel,
    isConnected,
    connectChannel: () => {
      if (channel === 'email') {
        setEmailConnected(true);
        return;
      }

      setTiktokDmConnected(true);
    },
    templateName,
    templateOptions: channel === 'email' ? EMAIL_TEMPLATE_OPTIONS : TIKTOK_DM_TEMPLATE_OPTIONS,
    setTemplateName: (value: string) => {
      if (channel === 'email') {
        setEmailTemplateName(value);
        return;
      }

      setTiktokDmTemplateName(value);
    },
    initialMessage,
    setInitialMessage: (message: SavedListOutreachMessageDTO) => {
      if (channel === 'email') {
        setInitialEmail(message);
        return;
      }

      setInitialDm(message);
    },
    followUpEmail,
    setFollowUpEmail,
    followUpEnabled,
    setFollowUpEnabled,
    aiRepliesEnabled,
    setAiRepliesEnabled,
    recipientIds,
    recipientLabel: getSavedListOutreachRecipientLabel({
      channel,
      selectedCount: selectedCreatorIds.length,
      recipientCount: recipientIds.length,
    }),
    request,
  };
}
